import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import CampaignCard from "@/components/CampaignCard";
import { CATEGORIES } from "@/data/campaigns";
import { fetchCampaigns } from "@/lib/api";

const PER_PAGE = 6;

const Explore = () => {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("trending");
  const [page, setPage] = useState(1);
  const activeCategory = params.get("category") || "all";
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const apiParams = {};
    if (activeCategory !== "all") apiParams.category = activeCategory;
    if (query) apiParams.q = query;
    apiParams.sort = sort === "trending" ? "raised" : sort;
    fetchCampaigns(apiParams).then((d) => { setCampaigns(d); setLoading(false); }).catch(() => setLoading(false));
  }, [activeCategory, query, sort]);

  const totalPages = Math.max(1, Math.ceil(campaigns.length / PER_PAGE));
  const paginated = useMemo(() => campaigns.slice((page - 1) * PER_PAGE, page * PER_PAGE), [campaigns, page]);

  const setCategory = (id) => {
    if (id === "all") setParams({});
    else setParams({ category: id });
    setPage(1);
  };

  return (
    <div data-testid="explore-page">
      <section className="border-b border-warm-200 bg-gradient-to-b from-slate-50 to-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
          <div className="max-w-3xl">
            <span className="text-sm font-medium text-saffron-600">Explore</span>
            <h1 className="mt-2 font-display text-4xl md:text-6xl font-semibold tracking-tight text-navy-800">Discover projects worth backing.</h1>
            <p className="mt-4 text-navy-600 text-lg">Live campaigns across ten categories. Curated weekly by our team.</p>
          </div>
          <div className="mt-8 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                data-testid="explore-search-input"
                placeholder="Search campaigns, creators, or keywords..."
                value={query}
                onChange={(e) => { setQuery(e.target.value); setPage(1); }}
                className="pl-11 h-12 rounded-2xl border-warm-200 bg-white"
              />
            </div>
            <Select value={sort} onValueChange={(v) => { setSort(v); setPage(1); }}>
              <SelectTrigger className="h-12 md:w-56 rounded-2xl border-warm-200 bg-white" data-testid="explore-sort-select">
                <SlidersHorizontal className="h-4 w-4 mr-2 text-navy-500" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="trending">Trending</SelectItem>
                <SelectItem value="raised">Most funded</SelectItem>
                <SelectItem value="backers">Most backers</SelectItem>
                <SelectItem value="ending">Ending soon</SelectItem>
                <SelectItem value="recent">Newest</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <button
              onClick={() => setCategory("all")}
              data-testid="filter-all"
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${activeCategory === "all" ? "bg-saffron-600 text-white" : "bg-white border border-warm-200 text-navy-700 hover:border-saffron-200 hover:text-saffron-600"}`}
            >
              All
            </button>
            {CATEGORIES.map((c) => (
              <button
                key={c.id}
                onClick={() => setCategory(c.id)}
                data-testid={`filter-${c.id}`}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${activeCategory === c.id ? "bg-saffron-600 text-white" : "bg-white border border-warm-200 text-navy-700 hover:border-saffron-200 hover:text-saffron-600"}`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="mb-6 text-sm text-navy-500">
          {loading ? "Loading..." : `${campaigns.length} campaign${campaigns.length !== 1 ? "s" : ""} found`}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="rounded-2xl border border-warm-200 bg-white overflow-hidden">
                <div className="aspect-[4/3] bg-slate-100 animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-slate-100 rounded animate-pulse" />
                  <div className="h-4 w-2/3 bg-slate-100 rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 p-16 text-center" data-testid="empty-state">
            <div className="font-display text-2xl font-semibold text-navy-800">No campaigns match your filters.</div>
            <p className="mt-2 text-navy-500">Try broadening your search or removing filters.</p>
            <Button className="mt-6 rounded-2xl bg-saffron-600 hover:bg-saffron-700" onClick={() => { setQuery(""); setCategory("all"); }}>Clear filters</Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {paginated.map((c, i) => <CampaignCard key={c.id} campaign={c} index={i} />)}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-12 flex items-center justify-center gap-2" data-testid="pagination">
            <Button variant="outline" disabled={page === 1} onClick={() => setPage(p => p - 1)} className="rounded-2xl">Previous</Button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                onClick={() => setPage(i + 1)}
                className={`h-10 w-10 rounded-xl text-sm font-medium transition-colors ${page === i + 1 ? "bg-saffron-600 text-white" : "text-navy-700 hover:bg-slate-100"}`}
              >
                {i + 1}
              </button>
            ))}
            <Button variant="outline" disabled={page === totalPages} onClick={() => setPage(p => p + 1)} className="rounded-2xl">Next</Button>
          </div>
        )}
      </section>
    </div>
  );
};

export default Explore;
